import React from "react";
import Markdown from "components/markdown/markdown";
import {
  TextButton,
  type TextButtonProps,
} from "components/textButton/TextButton";
import { CalloutDato } from "lib/types/homepage";

export type CalloutTitleSize = "small" | "large";

export type SharedCalloutProps = {
  callout: CalloutDato;
  href?: string;
  onClick?: () => void;
  className?: string;
  renderMarkdown?: boolean;
  fixedHeight?: boolean;
  titleSize?: CalloutTitleSize;
  centerContent?: boolean;
  buttonClassName?: string;
};

type TextButtonVariant = NonNullable<TextButtonProps["variant"]>;

const BORDER_COLOR_MAP: Record<string, string> = {
  sifted: "border-sifted",
  pro: "border-pro",
  fintech: "border-fintech",
  startuplife: "border-startuplife",
  climate: "border-climate",
  upround: "border-upround",
  deeptech: "border-deeptech",
  neutral: "border-mono-20",
};

const BG_COLOR_MAP: Record<string, string> = {
  sifted: "bg-sifted-10",
  pro: "bg-pro-10",
  fintech: "bg-fintech-10",
  startuplife: "bg-startuplife-10",
  climate: "bg-climate-10",
  upround: "bg-upround-10",
  deeptech: "bg-deeptech-10",
  neutral: "bg-mono-05",
};

const CALLOUT_ACCENT_TO_TEXT_BUTTON_VARIANT_MAP: Record<
  string,
  TextButtonVariant
> = {
  sifted: "sifted",
  pro: "pro",
  fintech: "fintech",
  startuplife: "startuplife",
  climate: "climate",
  upround: "upround",
  deeptech: "deeptech",
  neutral: "neutral",
  summit: "sifted",
};

const getCalloutAppearance = (accent: string) => {
  if (accent === "summit") {
    return {
      borderColor: "border-summit-yellow",
      bgColor: "bg-gradient-to-r from-black to-summit-charcoal",
      textColor: "text-white",
    };
  }

  return {
    borderColor: BORDER_COLOR_MAP[accent] || BORDER_COLOR_MAP.sifted,
    bgColor: BG_COLOR_MAP[accent] || BG_COLOR_MAP.sifted,
    textColor: "text-brand-neutral",
  };
};

const getTextButtonVariant = (accent: string): TextButtonVariant => {
  return (
    CALLOUT_ACCENT_TO_TEXT_BUTTON_VARIANT_MAP[accent] ||
    CALLOUT_ACCENT_TO_TEXT_BUTTON_VARIANT_MAP.sifted
  );
};

export const BaseCallout: React.FC<SharedCalloutProps> = ({
  callout,
  href,
  onClick,
  className,
  renderMarkdown = false,
  fixedHeight = false,
  titleSize = "large",
  centerContent = false,
  buttonClassName,
}) => {
  const { title, description, buttonLabel, accent } = callout;
  const { borderColor, bgColor, textColor } = getCalloutAppearance(accent);
  const titleSizeClass =
    titleSize === "small" ? "text-accent-16" : "text-accent-26";
  const containerClassName = [
    "flex flex-col gap-2 border-l p-4",
    borderColor,
    bgColor,
    fixedHeight ? "h-[152px]" : "",
    centerContent ? "justify-center" : "",
    className || "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClassName} data-testid="callout">
      <h3
        className={`font-recklessBold ${titleSizeClass} leading-tight ${textColor}`}
      >
        {title}
      </h3>

      {description &&
        (renderMarkdown ? (
          <div
            className={`text-regular-16 ${textColor} [&_li]:leading-tight [&_ul]:ml-6 [&_ul]:list-inside [&_ul]:list-disc`}
          >
            <Markdown text={description} />
          </div>
        ) : (
          <p className={`line-clamp-2 text-regular-16 ${textColor}`}>
            {description}
          </p>
        ))}

      {buttonLabel && (
        <TextButton
          label={buttonLabel}
          href={href}
          onClick={onClick}
          variant={getTextButtonVariant(accent)}
          iconPosition="right"
          className={buttonClassName}
        />
      )}
    </div>
  );
};

BaseCallout.displayName = "BaseCallout";

export default BaseCallout;
